
import { useMemo } from "react";
import { departmentSEOContent, DepartmentSEOContent } from "@/data/geographic/departmentContent";

export const useGeoLocalizedData = (departmentCode?: string, city?: string) => {
  return useMemo(() => {
    // Récupérer les données du département
    const departmentData = departmentCode ? departmentSEOContent[departmentCode] : null;
    
    // Générer des données structurées géolocalisées
    const generateLocalBusinessSchema = (content: DepartmentSEOContent, specificCity?: string) => {
      const targetCity = specificCity || content.mainCities[0];
      
      return {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "@id": `https://www.groupefabre-environnement.fr/services/${content.code}#localbusiness`,
        "name": `Groupe Fabre Environnement - ${content.name}`,
        "description": content.seoDescription,
        "url": `https://www.groupefabre-environnement.fr/services/departement/${content.code}`,
        "telephone": "+33565787900",
        "email": "contactgfe@groupefabre.com",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": targetCity,
          "addressRegion": content.name,
          "addressCountry": "FR",
          "postalCode": content.code + "000"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "44.3500",
          "longitude": "2.5833"
        },
        "areaServed": {
          "@type": "AdministrativeArea",
          "name": content.name,
          "containsPlace": content.mainCities.map(cityName => ({
            "@type": "City",
            "name": cityName
          }))
        },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": `Services de gestion des déchets ${content.name}`,
          "itemListElement": content.services.map((service, index) => ({
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": service.title,
              "description": service.description,
              "areaServed": {
                "@type": "AdministrativeArea",
                "name": content.name
              }
            }
          }))
        },
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "08:00",
            "closes": "17:00"
          }
        ],
        "priceRange": "€€",
        "paymentAccepted": ["Cash", "Credit Card", "Invoice"],
        "currenciesAccepted": "EUR"
      };
    };

    // Générer les métadonnées SEO géolocalisées
    const generateSEOMetadata = (content: DepartmentSEOContent, specificCity?: string) => {
      const targetCity = specificCity || content.mainCities[0];
      
      return {
        title: specificCity 
          ? `Gestion des déchets ${specificCity} (${content.code}) - Groupe Fabre Environnement`
          : content.seoTitle,
        description: specificCity
          ? `Services de gestion des déchets à ${specificCity} dans le ${content.name}. Location bennes, recyclage métaux, valorisation. Devis gratuit.`
          : content.seoDescription,
        keywords: [
          `gestion déchets ${content.name}`,
          `recyclage ${content.code}`,
          `benne ${targetCity}`,
          ...content.services.flatMap(s => s.keywords),
          `environnement ${content.name}`,
          `valorisation déchets ${content.code}`
        ].join(', ')
      };
    };

    return {
      departmentData,
      localBusinessSchema: departmentData ? generateLocalBusinessSchema(departmentData, city) : null,
      seoMetadata: departmentData ? generateSEOMetadata(departmentData, city) : null,
      availableDepartments: Object.keys(departmentSEOContent),
      isValidDepartment: departmentCode ? departmentCode in departmentSEOContent : false
    };
  }, [departmentCode, city]);
};
